/**
 * Bus de eventos tipado (punto 32 del brief).
 * Desacopla gameplay (Phaser) de UI, sistemas, sonidos y logros.
 * Ligero y sin dependencias: no usamos el EventEmitter de Phaser para
 * que los sistemas no queden atados al motor.
 */

export interface GameEvents {
  LOCATION_ENTERED: { location: string };
  INTERACTABLE_NEAR: { id: string | null; label?: string; verb?: string };
  INTERACT: { id: string; type: string };
  ITEM_PICKED: { itemId: string };
  HAZARD_DISCOVERED: { hazardId: string };
  EQUIPMENT_CHANGED: { slot: string; itemId: string | null };
  NPC_TALKED: { npcId: string };
  MISSION_STARTED: { missionId: string };
  MISSION_COMPLETED: { missionId: string };
  SECURITY_CHANGED: { location: string; value: number };
  TOAST: { title: string; desc: string };
}

type Handler<T> = (payload: T) => void;
// El cast a este tipo laxo queda CONTENIDO dentro del bus; la API pública
// (on/off/emit) es totalmente tipada por evento.
type AnyHandler = (payload: unknown) => void;

class TypedEventBus {
  private handlers = new Map<keyof GameEvents, Set<AnyHandler>>();

  on<K extends keyof GameEvents>(event: K, handler: Handler<GameEvents[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler as AnyHandler);
    return () => this.off(event, handler);
  }

  off<K extends keyof GameEvents>(event: K, handler: Handler<GameEvents[K]>): void {
    this.handlers.get(event)?.delete(handler as AnyHandler);
  }

  emit<K extends keyof GameEvents>(event: K, payload: GameEvents[K]): void {
    this.handlers.get(event)?.forEach((h) => h(payload));
  }
}

/** Instancia única compartida por todo el juego. */
export const bus = new TypedEventBus();
